import { useState } from 'react';

import { useMutation, useQuery } from 'react-query';

import { useHistory, useParams } from 'react-router-dom';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import * as yup from 'yup';

import { acceptInvite, getInvite } from '@services/queries/invites';

import LogoImage from '@assets/svg/standard-logo.svg';
import StatusSuccess from '@assets/svg/status-success.svg';
import StatusError from '@assets/svg/status-error.svg';

import { FormStatus } from '@components';

import { InviteUserPanel } from '@models/Invite.model';
import { Input } from './components';

import {
  Container,
  Body,
  Button,
  Description,
  Statuses,
  Email,
  Name,
  TitleResult,
  Requirements,
  Logo,
} from './Invite.styles';

interface IFormData {
  firstname: string;
  lastname: string;
  password: string;
  confirmPassword: string;
}

const Invite = () => {
  const history = useHistory();
  const params = useParams<{ id: string }>();

  const [isSuccess, setIsSuccess] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const mutation = useMutation(acceptInvite, {
    onSuccess: async () => {
      setIsSuccess(true);

      await new Promise(resolve => {
        setTimeout(resolve, 3000);
      });

      history.push('/');
    },
  });

  const minTest = yup.object().shape({
    password: yup.string().min(8),
  });

  const characterTest = yup.object().shape({
    password: yup
      .string()
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!.@#$%_^:&*-])/),
  });

  const matchingTest = yup.object().shape({
    password: yup.string(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match'),
  });

  const firstnameRules = yup.string().required();

  const lastnameRules = yup.string().required();

  const passwordRules = yup
    .string()
    .min(8)
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!.@#$%_^:&*-])/)
    .required();

  const confirmPasswordRules = yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required();

  const firstnameSchema = yup.object().shape({
    firstname: firstnameRules,
  });

  const lastnameSchema = yup.object().shape({
    lastname: lastnameRules,
  });

  const passwordSchema = yup.object().shape({
    password: passwordRules,
  });

  const confirmPasswordSchema = yup.object().shape({
    password: passwordRules,
    confirmPassword: confirmPasswordRules,
  });

  const schema = yup.object().shape({
    firstname: firstnameRules,
    lastname: lastnameRules,
    password: yup
      .string()
      .min(8)
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!.@#$%_^:&*-])/)
      .required(),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match')
      .required(),
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = (data: IFormData) => {
    if (!mutation.isLoading) {
      mutation.mutate({
        id: params.id,
        name: `${data.firstname} ${data.lastname}`,
        password: data.password,
      });
    }
  };

  const { data, isLoading, isError } = useQuery(
    ['invites', params.id],
    () => {
      return getInvite(params.id);
    },
    {
      retry: false,
      onError: (error: any) => {
        if (error?.response?.data?.message === 'Invite expired') {
          setIsExpired(true);
        }
        if (error?.response?.data?.message === 'Invite canceled') {
          setIsCanceled(true);
        }
      },
    },
  );

  if (isLoading) {
    return (
      <Container>
        <Body>
          <Logo src={LogoImage} alt="logo" />

          <div style={{ height: 32, flexShrink: 0 }} />

          <Email>Loading invite...</Email>
        </Body>
      </Container>
    );
  }

  if (isExpired) {
    return (
      <Container>
        <Body>
          <Logo src={LogoImage} alt="logo" />

          <div style={{ height: 32, flexShrink: 0 }} />

          <TitleResult>
            <img src={StatusError} alt="error" />
            <div style={{ width: 8 }} />
            <Email>Sorry, your invite has expried</Email>
          </TitleResult>

          <div style={{ height: 8, flexShrink: 0 }} />
          <Description>
            Please ask your admin to re-send your invitation
          </Description>
        </Body>
      </Container>
    );
  }

  if (isCanceled) {
    return (
      <Container>
        <Body>
          <Logo src={LogoImage} alt="logo" />

          <div style={{ height: 32, flexShrink: 0 }} />

          <TitleResult>
            <img src={StatusError} alt="error" />
            <div style={{ width: 8 }} />
            <Email>Sorry, your invite has been canceled</Email>
          </TitleResult>

          <div style={{ height: 8, flexShrink: 0 }} />
          <Description>
            Please ask your admin to re-send your invitation
          </Description>
        </Body>
      </Container>
    );
  }

  if (
    isError ||
    !data ||
    mutation.isError ||
    data.panel === InviteUserPanel.ADMIN
  ) {
    return (
      <Container>
        <Body>
          <Logo src={LogoImage} alt="logo" />

          <div style={{ height: 32, flexShrink: 0 }} />

          <TitleResult>
            <img src={StatusError} alt="error" />
            <div style={{ width: 8 }} />
            <Email>Sorry, something went wrong</Email>
          </TitleResult>

          <div style={{ height: 8, flexShrink: 0 }} />
          <Description>
            We’re sorry for the inconvenience, please try again later
          </Description>
        </Body>
      </Container>
    );
  }

  if (isSuccess) {
    return (
      <Container>
        <Body>
          <Logo src={LogoImage} alt="logo" />

          <div style={{ height: 32, flexShrink: 0 }} />

          <TitleResult>
            <img src={StatusSuccess} alt="success" />
            <div style={{ width: 8 }} />
            <Email>You’re all set!</Email>
          </TitleResult>

          <div style={{ height: 8, flexShrink: 0 }} />
          <Description>You’ll be redirected to Beam in a moment</Description>
        </Body>
      </Container>
    );
  }

  return (
    <Container>
      <Body>
        <Logo src={LogoImage} alt="logo" />

        <div style={{ height: 32, flexShrink: 0 }} />

        <Email>{`Hi ${data.email}`}</Email>

        <div style={{ height: 8, flexShrink: 0 }} />
        <Description>
          Create your {data?.studio ? data.studio.name : ''} account on Beam
        </Description>

        <div style={{ height: 24, flexShrink: 0 }} />

        <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
          <Name>
            <Input
              status={(() => {
                if (!firstname) return 'none';
                if (firstnameSchema.isValidSync({ firstname }))
                  return 'success';

                return 'error';
              })()}
              placeholder="First Name"
              {...register('firstname')}
              onChange={e => {
                register('firstname').onChange(e);
                setFirstname(e.currentTarget.value);
              }}
              autoFocus
            />

            <Input
              status={(() => {
                if (!lastname) return 'none';
                if (lastnameSchema.isValidSync({ lastname })) return 'success';

                return 'error';
              })()}
              placeholder="Last Name"
              {...register('lastname')}
              onChange={e => {
                register('lastname').onChange(e);
                setLastname(e.currentTarget.value);
              }}
            />
          </Name>

          <div style={{ height: 16, flexShrink: 0 }} />
          <Input
            status={(() => {
              if (!password) return 'none';
              if (passwordSchema.isValidSync({ password })) return 'success';

              return 'error';
            })()}
            placeholder="Password"
            {...register('password')}
            onChange={e => {
              register('password').onChange(e);
              setPassword(e.currentTarget.value);
            }}
            type="password"
          />
          <div style={{ height: 16, flexShrink: 0 }} />
          <Input
            status={(() => {
              if (!confirmPassword) return 'none';
              if (
                confirmPasswordSchema.isValidSync({ password, confirmPassword })
              )
                return 'success';

              return 'error';
            })()}
            placeholder="Confirm Password"
            {...register('confirmPassword')}
            onChange={e => {
              register('confirmPassword').onChange(e);
              setConfirmPassword(e.currentTarget.value);
            }}
            type="password"
          />

          <div style={{ height: 24, flexShrink: 0 }} />

          <Requirements>Requirements</Requirements>
          <div style={{ height: 8, flexShrink: 0 }} />

          <Statuses>
            <FormStatus
              status={(() => {
                if (!firstname && !lastname) return 'none';
                if (
                  firstnameSchema.isValidSync({ firstname }) &&
                  lastnameSchema.isValidSync({ lastname })
                ) {
                  return 'success';
                }

                return 'error';
              })()}
              label="First and Last Name"
            />
            <div style={{ height: 10, flexShrink: 0 }} />
            <FormStatus
              status={(() => {
                if (!password && !confirmPassword) return 'none';
                if (matchingTest.isValidSync({ password, confirmPassword }))
                  return 'success';

                return 'error';
              })()}
              label="Matching Passwords"
            />
            <div style={{ height: 10, flexShrink: 0 }} />
            <FormStatus
              status={(() => {
                if (!password && !confirmPassword) return 'none';
                if (minTest.isValidSync({ password })) return 'success';

                return 'error';
              })()}
              label="Password with at least 8 characters"
            />
            <div style={{ height: 10, flexShrink: 0 }} />
            <FormStatus
              status={(() => {
                if (!password && !confirmPassword) return 'none';
                if (characterTest.isValidSync({ password })) return 'success';

                return 'error';
              })()}
              label="Password must contain at least one lowercase, uppercase, number, and special character"
            />
          </Statuses>

          <div style={{ height: 24, flexShrink: 0 }} />

          <Button disabled={!isValid || mutation.isLoading}>Create</Button>
        </form>
      </Body>
    </Container>
  );
};

export { Invite };
